import { create } from "zustand";
import { persist } from "zustand/middleware";

const BASE = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:4000";

export interface AuthUser {
  id: number;
  email: string;
  fullName: string;
  roles: string[];
  permissions: string[];
  ginaIdcompany: number | null;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  mustChangePassword: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  tryRefresh: () => Promise<boolean>;
  clearMustChange: () => void;
  hasPermission: (perm: string) => boolean;
}

/**
 * El refresco en curso, que comparten todos los pedidos que reciben 401 a la vez. El API rota el refresh token: con
 * dos POST /auth/refresh en paralelo, el segundo fallaría y cerraría la sesión, y su logout revocaría el token nuevo
 * que acaba de guardar el primero.
 */
let refrescoEnCurso: Promise<boolean> | null = null;

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      mustChangePassword: false,

      async login(email, password) {
        const res = await fetch(`${BASE}/auth/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password, rememberMe: true }),
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data?.error?.message ?? "Error al iniciar sesión");
        }
        set({
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          user: data.user,
          mustChangePassword: Boolean(data.mustChangePassword),
        });
      },

      clearMustChange() {
        set({ mustChangePassword: false });
      },

      logout() {
        const rt = get().refreshToken;
        if (rt) {
          fetch(`${BASE}/auth/logout`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ refreshToken: rt }),
          }).catch(() => undefined);
        }
        set({
          accessToken: null,
          refreshToken: null,
          user: null,
          mustChangePassword: false,
        });
      },

      tryRefresh() {
        refrescoEnCurso ??= (async () => {
          const rt = get().refreshToken;
          if (!rt) return false;
          try {
            const res = await fetch(`${BASE}/auth/refresh`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ refreshToken: rt }),
            });
            if (!res.ok) return false;
            const data = await res.json();
            set({
              accessToken: data.accessToken,
              refreshToken: data.refreshToken,
              user: data.user,
              mustChangePassword: Boolean(data.mustChangePassword),
            });
            return true;
          } catch {
            return false;
          }
        })().finally(() => {
          refrescoEnCurso = null;
        });
        return refrescoEnCurso;
      },

      hasPermission(perm) {
        return get().user?.permissions.includes(perm) ?? false;
      },
    }),
    {
      name: "bo-auth",
      partialize: (s) => ({
        accessToken: s.accessToken,
        refreshToken: s.refreshToken,
        user: s.user,
        mustChangePassword: s.mustChangePassword,
      }),
    },
  ),
);
